<script setup>
import { onMounted, useTemplateRef, computed, useAttrs } from "vue";
import { useScrollStore } from "@/stores/storeScroll";
import { useSettingsStore } from "@/stores/storeSettings";
import { useFieldsStore } from "@/stores/storeFieldData";
import { useRequestsStore } from "@/stores/storeRequests";
import { useModals } from "@/composables/useModals";
import { RouterLink, useRoute, useRouter } from "vue-router";
import { sendMetrik, trackPixel, trackAdriver } from "@/utils/metrika";
import BaseButtonUploadReceipt from "@/components/ui/BaseButtonUploadReceipt.vue";

const router = useRouter();

const { openModal } = useModals();
const storeScroll = useScrollStore();
const storeRequests = useRequestsStore();
const storeSettings = useSettingsStore();
const storeField = useFieldsStore();
const route = useRoute();

const header = useTemplateRef("header");
const userName = computed(() => {
	if (storeRequests.userData.first_name === undefined) {
		return "Профиль";
	} else {
		return `${storeRequests.userData?.first_name} ${storeRequests.userData.last_name?.slice(0, 1)}.`;
	}
});

const userLeaves = computed(() => storeRequests.userData?.leaves || 0);

const isProfilePage = computed(() => (route.name === "profile" ? "--profile-page" : ""));
const isParkGorkogoPage = computed(() => route.name === "parkgorkogo");

const onNavLinkClick = (link) => {
	if (isParkGorkogoPage.value && link.href === "participation") {
		sendMetrik("reachGoal", "how_to");
		trackAdriver("how_to");
	} else {
		sendMetrik("reachGoal", "click_header");
	}

	mobileMenu();
	trackPixel("VIEW", "tracking");
};

const links = [
	{
		href: "participation",
		label: "Как принять участие",
	},
	{
		href: "prizes",
		label: "Призы",
	},
	{
		href: "winners",
		label: "Победители",
	},
	{
		href: "faq",
		label: "Вопросы-ответы",
	},
];

const onMetrica = (_key) => {
	// switch (_key) {
	// 	case "participation":
	// 		sendMetrik("reachGoal", "klik_header_participate");
	// 		break;
	// 	case "prizes":
	// 		sendMetrik("reachGoal", "klik_header_prizes");
	// 		break;
	// 	case "winners":
	// 		sendMetrik("reachGoal", "klik_header_winners");
	// 		break;
	// 	case "faq":
	// 		sendMetrik("reachGoal", "klik_header_faq");
	// 		break;
	// }
};

defineProps({
	pageLoaded: {
		type: Boolean,
	},
});
const emit = defineEmits(["mobileMenuShow"]);
defineOptions({ inheritAttrs: false });
const attrs = useAttrs();

const mobileMenu = () => {
	const width = storeSettings.widthWindow || window.innerWidth;
	if (width > 992) return;
	storeSettings.mobileMenuOpen = !storeSettings.mobileMenuOpen;

	emit("mobileMenuShow");
	if (storeSettings.mobileMenuOpen) {
		document.body.classList.add("noscroll");
	} else {
		document.body.classList.remove("noscroll");
	}
};

const clickBtnProfile = () => {
	if (storeSettings.statusStockFuture) {
		openModal("promoNotStart");
	}

	if (storeSettings.statusStockPast || storeSettings.statusStockActive) {
		openModal("authorizationRequired");
	}
	// sendMetrik("reachGoal", "klik_button_profile");
};

const clickUploadReceipt = () => {
	if (!storeRequests.isAuthenticated) {
		if (storeSettings.statusStockFuture) {
			openModal("promoNotStart");
		} else {
			openModal("authorizationRequired");
		}
	} else {
		openModal("uploadReceipt");
	}
};

onMounted(async () => {
	storeScroll.header = header.value;
});
</script>
<template>
	<div id="overlay" @click="mobileMenu" :class="{ active: storeSettings.mobileMenuOpen }"></div>
	<header v-bind="attrs" :class="['header', isProfilePage, { '--fixed': storeScroll.headerFixed }, { active: storeSettings.mobileMenuOpen }]" ref="header">
		<div class="container --big">
			<div class="header__container">
				<RouterLink to="/" class="logo">
					<img width="284" height="139" src="@/assets/img/icons/logo.svg" alt="Greenfield" class="logo__img" />
				</RouterLink>

				<nav class="nav">
					<RouterLink :to="`/#${link.href}`" :class="['nav__link']" @click="onNavLinkClick(link)" v-for="link in links" :key="link.href">
						{{ link.label }}
					</RouterLink>

					<div class="header__btns --mobile">
						<button class="header__btn --profile btn --small --icon" v-if="!storeRequests.isAuthenticated" @click="clickBtnProfile()">
							<svg width="32" height="32"><use href="#user"></use></svg>
							<span>Профиль</span>
						</button>
						<RouterLink @click="mobileMenu" class="header__btn --profile btn --small --icon" to="/profile" v-else>
							<svg width="32" height="32"><use href="#user"></use></svg>
							<span>{{ userName }}</span>
						</RouterLink>
						<button v-if="storeRequests.isAuthenticated" @click="(storeRequests.logout(), mobileMenu())" class="header__btn --logout btn --small --icon">
							<svg width="32" height="32"><use href="#logout"></use></svg>
						</button>
					</div>
				</nav>

				<div class="header__btns --desktop">
					<button class="header__btn --profile btn --small --icon" v-if="!storeRequests.isAuthenticated" @click="clickBtnProfile()">
						<svg width="32" height="32"><use href="#user"></use></svg>
						<span>Профиль</span>
					</button>
					<RouterLink class="header__btn --profile btn --small --icon" to="/profile" v-else>
						<svg width="32" height="32"><use href="#user"></use></svg>
						<span>{{ userName }}</span>
					</RouterLink>
					<button v-if="storeRequests.isAuthenticated" @click="storeRequests.logout()" class="header__btn --logout btn --small --icon">
						<svg width="32" height="32"><use href="#logout"></use></svg>
					</button>
				</div>
				<div @click="mobileMenu" :class="['nav-icon', { active: storeSettings.mobileMenuOpen }]"><span></span></div>
			</div>
		</div>
	</header>
</template>
<style lang="scss" scoped>
@use "@/assets/scss/base/vars";
@use "@/assets/scss/blocks/nav-icon";

.header {
	position: fixed;
	width: 100%;
	top: 0;
	left: 0;
	background-color: transparent;
	z-index: 4;
	transition: 0.2s ease-in;

	&.--fixed {
		background: var(--fill-accent-1-1);
		.logo {
			&__img {
				width: 18.8rem;
				@media (max-width: vars.$l) {
					width: 14rem;
				}
				@media (max-width: vars.$mn) {
					width: 12rem;
				}
			}
		}
	}

	&.active {
		.nav {
			left: 0;
			opacity: 1;
		}
	}
}

.header__container {
	width: 100%;
	display: flex;
	justify-content: space-between;
	align-items: center;
}

.logo {
	position: relative;
	z-index: 3;
	flex-shrink: 0;

	@media (max-width: vars.$x480) {
		left: 50%;
		transform: translateX(-50%);
	}

	&__img {
		width: 28.4rem;
		height: auto;
		transition: var(--transition) ease-in;
		@media (max-width: vars.$l) {
			width: 18rem;
		}
		@media (max-width: vars.$mn) {
			width: 14.1rem;
		}
	}
}

.nav {
	display: flex;
	align-items: center;
	gap: 2.6rem;

	@media (max-width: vars.$lg) {
		gap: 2rem;
	}

	@media (max-width: vars.$mn) {
		position: fixed;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		z-index: 2;
		top: 0;
		left: -100%;
		opacity: 0;
		padding: 12.4rem var(--padding-container) 2rem;
		background: var(--fill-accent-1-1);
		width: 100%;
		max-width: 100%;
		gap: 2rem;
		transition: all 0.5s ease-in;
		overflow-y: auto;
		height: 100dvh;
	}
}

.nav__link {
	font-family: var(--font-family);
	font-weight: 600;
	font-size: 2rem;
	line-height: 2.4rem;
	color: var(--text-inverse-100);
	white-space: nowrap;
	transition: opacity var(--transition);

	@media (hover: hover) {
		&:hover {
			opacity: 0.7;
		}
	}

	@media (max-width: vars.$lg) {
		// font-size: 1.6rem;
		// line-height: 2rem;
	}

	@media (max-width: vars.$l) {
		// font-size: 2rem;
		// line-height: 2.4rem;
		// padding: 1.2rem 0;
	}
}

.header__btns {
	display: flex;
	align-items: center;
	gap: 1rem;
	flex-shrink: 0;

	@media (max-width: vars.$l) {
		gap: 0.8rem;
	}

	&.--desktop {
		@media (max-width: vars.$mn) {
			display: none;
		}
	}

	&.--mobile {
		display: none;
		width: 100%;
		gap: 1.2rem;
		margin-top: auto;
		padding-top: 2rem;

		@media (max-width: vars.$mn) {
			display: flex;
			justify-content: center;
		}

		.header__btn {
		}
	}
}

.header__btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 0.8rem;
	
	svg {
		width: 3.2rem;
		height: 3.2rem;
		flex-shrink: 0;
		fill: transparent;
		stroke: currentColor;

		@media (max-width: vars.$lg) {
			// width: 2.4rem;
			// height: 2.4rem;
		}
	}

	&.--upload {
		@media (max-width: vars.$l) {
			order: 3;
		}
	}

	&.--profile {
		@media (max-width: vars.$mn) {
			width: 30%;
		}
		@media (max-width: vars.$xx) {
			width: 65%;
		}
		@media (max-width: vars.$x480) {
			        width: 100%;
		}
		svg {
			stroke: var(--text-accent-1-100);
		}
		span {
			max-width: 14rem;
			overflow: hidden;
			text-overflow: ellipsis;
		}
	}

	&.--logout {
		padding-left: 0.7rem;
		padding-right: 0.7rem;
		svg {
			stroke: var(--text-accent-1-100);
		}
	}

	&.--leaves {
		svg {
			fill: var(--text-accent-1-100);
			stroke: none;
		}
	}
}

@keyframes animHeader {
	0% {
		transform: translateY(-100%);
	}
	100% {
		transform: translateY(0%);
	}
}
</style>
